// © Microsoft Corporation. All rights reserved.

import { SortBoxTelemetryObjects, sortBoxTelemetryActionDispatchers, sortBoxTelemetryContracts } from "./SortBox.telemetry";
import { addOrUpdateTelemetryObject, renderHybrid } from "../../Social.telemetry";
import { chevron_down, tick } from "../../utils/icons";

import AccessibilityHelpers from "../../utils/AccessibilityHelpers";
import Popover from "../popover/Popover";
import React from "react";
import { SocialClassNameContract } from "../../Social.classnames";
import { SocialLocalizedStrings } from "../../Social.localizedStrings";
import { TelemetryObjectBase } from "@msnews/telemetry-contracts";

export interface SortBoxProps {
    id: string;
    onChange: (sortType: string) => void;
    managedClasses?: SocialClassNameContract;
    t: SocialLocalizedStrings;
    mainTelemetryObject: TelemetryObjectBase;
    selectedSortType: string;
    sortTypes: object;
    dontShowLabel?: boolean;
}

interface SortBoxState {
    listVisible: boolean
}

export default class SortBox extends React.Component<SortBoxProps, SortBoxState> {
    sortMenuTriggerRef: React.RefObject<HTMLDivElement>;
    sortMenuListItemRef: HTMLLIElement;
    telemetryObjects: SortBoxTelemetryObjects;

    constructor(props) {
        super(props);

        this.state = {
            listVisible: false
        };

        this.sortMenuTriggerRef = React.createRef();
        this.sortMenuListItemRef = null;
        this.telemetryObjects = {
            sortItems: []
        };
    }

    onLinkClick(e) {
        this.setState({
            listVisible: true
        });
    }

    changeSortHandler(e, type) {

        this.props.onChange(type);
        this.setState({
            listVisible: false
        });

        sortBoxTelemetryActionDispatchers.genericClicked(e.currentTarget, this.telemetryObjects && this.telemetryObjects.sortItems && this.telemetryObjects.sortItems[type]);
        const ref = window.document.getElementById(`sortMenu_${this.props.id}`);
        if (ref) {
            window.setTimeout(() => { ref.focus(); }, 50);
        }
    }

    handleKeyDown(e) {
        if (e.keyCode == 27) { /* ESC key */
            e.preventDefault();
            e.stopPropagation();
            const ref = window.document.getElementById(`sortMenu_${this.props.id}`);
            if (ref) {
                window.setTimeout(() => { ref.focus(); }, 50);
            }
        } else if (e.keyCode == 38) { /* Up arrow */
            e.preventDefault();
            e.stopPropagation();
            const prev = e.target.previousElementSibling;
            if (prev) {
                prev.focus();
            } else {
                e.target.parentElement.lastElementChild.focus();
            }
        } else if (e.keyCode == 40)  { /* Down arrow */
            const next = e.target.nextElementSibling;
            if (next) {
                next.focus();
            } else {
                e.target.parentElement.firstElementChild.focus();
            }
            e.preventDefault();
            e.stopPropagation();
        }
    }

    componentDidUpdate() {
        AccessibilityHelpers.attachAccessibilityListeners(document);
    }

    render() {
        const { id, t, managedClasses, mainTelemetryObject, selectedSortType, sortTypes, dontShowLabel } = this.props;
        const { listVisible } = this.state;

        return (
            <div className={`${managedClasses.sortBoxContainer} no-select`} onMouseDown={this.onLinkClick.bind(this)} ref={this.sortMenuTriggerRef}>
                {!dontShowLabel && t.CommentSortByTxt + ":"}
                {listVisible &&
            <Popover
                {...{
                    visible: listVisible,
                    triggerRef: this.sortMenuTriggerRef,
                    onHide: () => this.setState({listVisible: false}),
                    className: managedClasses.sortBoxPopover,
                    managedClasses: managedClasses,
                    hasFocusLock: true
                }}
            >
                <ul className="cwt-sort-list" role="listbox" aria-label={t.CommentSortByTxt}>
                    {Object.keys(sortTypes).map((type, index) => {

                        if (mainTelemetryObject) {
                            this.telemetryObjects.sortItems[type] = addOrUpdateTelemetryObject(mainTelemetryObject, sortBoxTelemetryContracts.sortItem, { id: mainTelemetryObject.contract.content.id, headline: type, type: mainTelemetryObject.contract.content.type }, this.telemetryObjects && this.telemetryObjects.sortItems[type]);
                        }
                        return <li
                            key={type}
                            role="option"
                            aria-selected={(type == selectedSortType)? "true": "false"}
                            className={`cwt-sort-list-item ${type == selectedSortType && "active"}`}
                            onClick={(e) => this.changeSortHandler(e, type)}
                            onKeyDown={(e) => this.handleKeyDown(e)}
                            {...renderHybrid(this.telemetryObjects && this.telemetryObjects.sortItems && this.telemetryObjects.sortItems[type], false, (el) => { (index==0) && (this.sortMenuListItemRef = el); })}
                        >
                            <i className="cwt-icon">
                                <span className="cwt-icon-label">{t[sortTypes[type]]}</span>
                                {type == selectedSortType && tick}
                            </i>
                        </li>;
                    }
                    )}
                </ul>
            </Popover>
                }
                <div className="cwt-sort-value-container" role="listbox"  aria-haspopup="true" aria-expanded={this.state.listVisible} aria-label={t.CommentSortByTxt}>
                    <span id={`sortMenu_${id}`} className="cwt-sort-value cwt-link">
                        {t[sortTypes[selectedSortType]]}
                        {chevron_down}
                    </span>
                </div>
            </div>
        );
    }
}